import { useInfiniteQuery } from "@tanstack/react-query";
import { screenPlaylistService } from "../api/services/schedularService";
import type {
  AllSchedulesFilters,
  AllSchedulesResult,
  ScheduleBatchItem,
} from "../schemas/scheduleSchema";
import { DEFAULT_PAGE_SIZE } from "../constants";

export type AllSchedulesInfiniteFilters = Omit<
  AllSchedulesFilters,
  "page" | "pageSize"
> & {
  pageSize?: number;
};

export const useAllSchedulesInfinite = (
  filters: AllSchedulesInfiniteFilters = {},
) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;

  const query = useInfiniteQuery<AllSchedulesResult, Error>({
    queryKey: ["all-schedules", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      screenPlaylistService.getAllSchedules({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
  });

  const schedules: ScheduleBatchItem[] =
    query.data?.pages.flatMap((p) => p.schedules) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    schedules,
    total,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
